// Relative path: src/components/Recommendations/FeaturedReviewCard.js

import React from 'react';

/**
 * Clickable website source component for featured review cards
 */
const ClickableReviewSource = React.memo(({ 
  sourceName, 
  sourceLogo, 
  onClick 
}) => {
  const handleClick = (e) => {
    e.stopPropagation(); // Prevent any parent click handlers
    if (onClick) {
      onClick();
    }
  };

  return (
    <button
      onClick={handleClick}
      className="text-sm text-gray-400 hover:text-gray-600 transition-colors duration-200 flex items-center"
    >
      <span className="underline text-xs md:text-base">View source</span>
      <span className="text-gray-600 text-xs md:text-base">&nbsp;{sourceName}</span>
      {sourceLogo && (
        <img
          src={sourceLogo}
          alt={`${sourceName} logo`}
          className="w-4 h-4 ml-1 object-contain"
        />
      )}
    </button>
  );
});

/**
 * Individual Featured Review Card Component
 * Displays a single featured review with before/after photos, reviewer info, rating, and quote
 */
const FeaturedReviewCard = ({
  reviewerName = "GeneralKenobi20",
  reviewerAvatar = "https://placehold.co/40x40/4285f4/ffffff?text=GK",
  sourceName = "reddit",
  sourceLogo = "https://placehold.co/20x20/ff4500/ffffff?text=R",
  beforeImage = "https://placehold.co/160x120/cccccc/666666?text=Before",
  afterImage = "https://placehold.co/160x120/cccccc/666666?text=After",
  rating = 4,
  maxRating = 5,
  tags = ["no medication"],
  reviewText = "One thing that did ease my mind was that when I met the Dr I told him I'd have more grafts done as I had the money, he said there was...",
  highlightText = "similar hair type to you",
  onClick = null,
  className = ""
}) => {
  // Render star rating
  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= maxRating; i++) {
      stars.push(
        <svg
          key={i}
          className={`w-6 h-6 ${i <= rating ? 'text-gray-600 fill-current' : 'text-gray-300 fill-current'}`}
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      );
    }
    return stars;
  };

  return (
      <div 
        className={`bg-transparent rounded-xl border-2 border-gray-200 px-5 py-3 ${className}`}
      >
      {/* Highlight bubble at top */}
      <div className="mb-3">
        <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-gray-600 text-sm font-medium">
          <svg className="w-4 h-4 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {highlightText}
        </div>
      </div>

      {/* Before/After Images - Side by Side with 100:71 aspect ratio */}
      <div className="mb-4">
        <div className="grid grid-cols-2 gap-1">
          {/* Before Image */}
          <div className="relative aspect-[71/100]">
            <img
              src={beforeImage}
              alt="Before"
              className="w-full h-full object-cover rounded-l-lg"
            />
          </div>
          
          {/* After Image */}
          <div className="relative aspect-[71/100]">
            <img
              src={afterImage}
              alt="After"
              className="w-full h-full object-cover rounded-r-lg"
            />
          </div>
        </div>
      </div>

      {/* Reviewer info below images */}
      <div className="flex items-center mb-2">
        <img
          src={reviewerAvatar}
          alt={reviewerName}
          className="w-10 h-10 rounded-full object-cover mr-3"
        />
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 text-base">{reviewerName}</h4>
          <div className="mt-1">
            <ClickableReviewSource
              sourceName={sourceName}
              sourceLogo={sourceLogo}
              onClick={onClick}
            />
          </div>
        </div>
      </div>

      <div className="w-full border-t border-gray-200 my-3" />

      {/* Rating and Tags - Combined in one row when possible */}
      <div className="mb-3">
        {/* Try to fit rating and tags in same row, otherwise stack them */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Rating Stars (removed X/5 text) */}
          <div className="flex items-center">
            {renderStars()}
          </div>
          
          {/* Tags */}
          {tags && tags.length > 0 && tags.map((tag, index) => {
            // Handle both string tags (legacy) and object tags (new format)
            const isObject = typeof tag === 'object';
            const tagText = isObject ? tag.text : tag;
            const isPositive = isObject ? tag.positive : true; // Default to positive for string tags
            
            return (
              <span
                key={index}
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  isPositive 
                    ? 'bg-blue-100 text-gray-500' 
                    : 'bg-blue-100 text-gray-500'
                }`}
              >
                {isPositive ? (
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                {tagText}
              </span>
            );
          })}
        </div>
      </div>

      {/* Review Text - Limited to 4 lines with ellipsis */}
      <p 
        className="text-gray-700 text-sm leading-relaxed mb-2"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: 4,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        {reviewText}
      </p>

      {/* Show Review link */}
{/*       <a 
        href={onClick ? '#' : undefined} // fallback if no URL
        onClick={(e) => {
          e.preventDefault();
          if (onClick) onClick();
        }}
        className="font-bold text-sm text-gray-600 underline hover:text-gray-800"
      >
        Show Review
      </a> */}

    </div>
  );
};

export default FeaturedReviewCard;